import React, { FC } from 'react'
import { When } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { slugs } from '@/constants/slugs'
import { PortraitRowCollection } from '@/molecules/PortraitRowCollection'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import {
  buildLinkTabModel,
  buildLinkTabUrl,
  buildTabSubtitle,
  ComingSoonNode,
  shortenMonthInHeader,
} from '@/services/ComingSoonService'
import { useGuildMember } from '@/services/GuildUserService'
import { useTranslate } from '@/utils/translate/translate-client'
import { LargePortraitLink } from '../CollectionDisplay/LargePortraitLink'

interface ComingSoonCollectionProps {
  ComingSoonTabCollection?: ComingSoonNode[]
  testId?: string
  containerPosition: number
}

export const ComingSoonCollection: FC<ComingSoonCollectionProps> = ({
  ComingSoonTabCollection,
  testId,
  containerPosition,
}) => {
  const { t } = useTranslate('watch')
  const { isGuildMember, loading } = useGuildMember()

  // Hover/impression tracking refs for discover interactions
  const hoverStartTimeRef = React.useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = React.useRef<Set<string>>(new Set())

  const canDisplay = ComingSoonTabCollection && ComingSoonTabCollection?.length > 0
  const plannedTheatricalReleases = new Set([slugs.fablehaven, slugs.fellowship, slugs.zeroAd])

  return (
    <>
      <When condition={canDisplay}>
        <div className="flex h-full flex-col gap-y-12">
          <PortraitRowCollection testId={testId} title={t('comingSoonAngel', 'Coming Soon to Angel')} haltLazyLoad>
            {ComingSoonTabCollection?.map((item, itemIndex) => {
              if (!item) return null

              if (
                item?.watchable?.__typename !== 'ContentMovie' &&
                item?.watchable?.__typename !== 'ContentSeries' &&
                item?.watchable?.__typename !== 'ContentSpecial' &&
                item?.watchable?.__typename !== 'ContentSeason'
              ) {
                return null
              }

              if (
                item?.focus?.__typename === 'TheatricalTicketsSaleFocus' ||
                (item?.project?.slug && plannedTheatricalReleases.has(item?.project?.slug))
              ) {
                return null
              }

              const linkModel = buildLinkTabModel(item, true)
              if (!linkModel) return null

              const title = item.title ?? item?.watchable?.name ?? item?.project?.name
              const subTitle = buildTabSubtitle(item, t)

              const header = item.header && shortenMonthInHeader(item.header)

              const createInteraction = (link: typeof linkModel, idx: number) => {
                const id = link.id ?? undefined
                const imageUrl = link.imageUrl ?? undefined
                const typename = item?.watchable?.__typename as
                  | 'ContentEpisode'
                  | 'ContentLivestream'
                  | 'ContentSeries'
                  | 'ContentMovie'
                  | 'ContentSpecial'
                  | undefined

                if (!id || !imageUrl || !typename) return null

                return new DiscoverItemInteraction({
                  id,
                  __typename: typename,
                  attribution: link.attribution,
                })
                  .withSurface('discover')
                  .withPlacement('coming_soon', containerPosition, idx, 'horizontal_swimlane')
                  .withImageArtwork(imageUrl, 'portrait_display')
              }

              return (
                <div className="block h-full" key={item?.id}>
                  <div className="mb-2 mr-4">
                    <LargePortraitLink
                      link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }}
                      itemIndex={itemIndex}
                      createInteraction={createInteraction}
                      hoverStartTimeRef={hoverStartTimeRef}
                      impressionTimeoutRef={impressionTimeoutRef}
                      hasTrackedImpression={hasTrackedImpression}
                      isGuildMember={isGuildMember}
                      loading={loading}
                    />
                  </div>
                  <TitleAndSubtitleDisplay
                    className="mr-4"
                    title={title || undefined}
                    subtitle={header ? `${header}` : subTitle}
                  />
                  <div className="flex min-h-[24px] items-center">
                    <When condition={item?.project?.highestScore}>
                      <GuildScore guildScore={item.project.highestScore} />
                    </When>
                  </div>
                </div>
              )
            })}
          </PortraitRowCollection>
          <PortraitRowCollection
            testId={testId}
            title={t('upcomingTheatricalReleases', 'Upcoming Theatrical Releases')}
            haltLazyLoad
          >
            {ComingSoonTabCollection?.map((item, itemIndex) => {
              if (!item) return null

              if (
                item?.watchable?.__typename !== 'ContentMovie' &&
                item?.watchable?.__typename !== 'ContentSeries' &&
                item?.watchable?.__typename !== 'ContentSpecial' &&
                item?.watchable?.__typename !== 'ContentSeason'
              ) {
                return null
              }

              if (
                item?.focus?.__typename !== 'TheatricalTicketsSaleFocus' &&
                item?.project?.slug &&
                !plannedTheatricalReleases.has(item?.project?.slug)
              ) {
                return null
              }

              const linkModel = buildLinkTabModel(item, true)
              if (!linkModel) return null

              const title = item.title ?? item?.watchable?.name ?? item?.project?.name
              const subTitle = buildTabSubtitle(item, t)

              const header = item.header && shortenMonthInHeader(item.header)

              const createInteraction = (link: typeof linkModel, idx: number) => {
                const id = link.id ?? undefined
                const imageUrl = link.imageUrl ?? undefined
                const typename = item?.watchable?.__typename as
                  | 'ContentEpisode'
                  | 'ContentLivestream'
                  | 'ContentSeries'
                  | 'ContentMovie'
                  | 'ContentSpecial'
                  | undefined

                if (!id || !imageUrl || !typename) return null

                return new DiscoverItemInteraction({
                  id,
                  __typename: typename,
                  attribution: link.attribution,
                })
                  .withSurface('discover')
                  .withPlacement('coming_soon', containerPosition, idx, 'horizontal_swimlane')
                  .withImageArtwork(imageUrl, 'portrait_display')
              }

              return (
                <div className="block h-full" key={item?.id}>
                  <div className="mb-2 mr-4">
                    <LargePortraitLink
                      link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }}
                      itemIndex={itemIndex}
                      createInteraction={createInteraction}
                      hoverStartTimeRef={hoverStartTimeRef}
                      impressionTimeoutRef={impressionTimeoutRef}
                      hasTrackedImpression={hasTrackedImpression}
                      isGuildMember={isGuildMember}
                      loading={loading}
                    />
                  </div>
                  <TitleAndSubtitleDisplay
                    className="mr-4"
                    title={title || undefined}
                    subtitle={header ? `${header}` : subTitle}
                  />
                  <div className="flex min-h-[24px] items-center">
                    <When condition={item?.project?.highestScore}>
                      <GuildScore guildScore={item.project.highestScore} />
                    </When>
                  </div>
                </div>
              )
            })}
          </PortraitRowCollection>
        </div>
      </When>
    </>
  )
}
